import { useState, useEffect } from "react";
import "./AdminOrganizationDetail.css";
import { OrganizationWorkspaceEntry as SupportSessionEntry } from "../support/OrganizationWorkspace";
import { useParams, useNavigate } from "react-router-dom";
import { adminOrgBase } from "../config/adminPortalRoutes";
import { useAuth, API } from "../App";

import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Building2,
  ArrowLeft,
  Mail,
  Phone,
  Globe,
  MapPin,
  Users,
  Trophy,
  Calendar,
  DollarSign,
  Settings,
  FileText,
  CreditCard,
  ExternalLink,
  Loader2,
  ChevronRight,
  Percent,
  Save,

  Activity,
  Clock,
  Shield,
  BarChart3,
  PieChart,
} from "lucide-react";
import { createExcerpt } from "../lib/contentSafety";
import AdminLayout from "../components/AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const PageFrame = ({ embedded, children }) => embedded
  ? children
  : <AdminLayout title="Organization Details">{children}</AdminLayout>;

const AdminOrganizationDetail = ({ embedded = false }) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [failedLogo, setFailedLogo] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [transactionFee, setTransactionFee] = useState("");
  const [savingFee, setSavingFee] = useState(false);

  const [stats, setStats] = useState({
    totalPrograms: 0,
    activePrograms: 0,
    totalRegistrations: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    pendingCount: 0,
    paidCount: 0,
    recentRegistrations: 0,
    teamsCount: 0,
    admins: 0,
    stripe: null,
    coaches: 0,
    players: 0,
    avgRegistrationValue: 0,
    conversionRate: 0
  });

  useEffect(() => {
    fetchOrgDetails();
  }, [orgId, token]);

  const fetchOrgDetails = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [orgRes, statsRes, programsRes] = await Promise.all([
        axios.get(`${API}/organizations/${orgId}`, { headers, params: { include_images: true } }),
        axios.get(`${API}/organizations/${orgId}/stats`, { headers }),
        axios.get(`${API}/programs?org_id=${orgId}`, { headers, params: { page: 1, page_size: 100 } })
      ]);
      
      setOrg(orgRes.data);
      setTransactionFee(orgRes.data.custom_transaction_fee?.toString() || orgRes.data.transaction_fee_percent?.toString() || "");
      
      // Handle paginated response
      const programsData = programsRes.data.items || programsRes.data;
      const orgPrograms = Array.isArray(programsData) ? programsData.filter(p => p.org_id === orgId) : [];
      setPrograms(orgPrograms);
      
      const statsData = statsRes.data;
      const avgValue = statsData.total_registrations > 0 
        ? statsData.total_revenue / statsData.total_registrations 
        : 0;
      
      setStats({
        totalPrograms: statsData.total_programs,
        activePrograms: statsData.active_programs,
        totalRegistrations: statsData.total_registrations,
        totalRevenue: statsData.total_revenue,
        pendingPayments: statsData.pending_amount,
        pendingCount: statsData.pending_registrations,
        paidCount: statsData.paid_registrations,
        recentRegistrations: statsData.recent_registrations,
        teamsCount: statsData.teams_count || 0,
        admins: statsData.admins_count,
        stripe: statsData.stripe,
        coaches: statsData.coaches_count || 0,
        players: statsData.players_count || 0,
        avgRegistrationValue: avgValue,
        conversionRate: statsData.conversion_rate || 0
      });
    } catch (error) {
      console.error("Error fetching organization:", error);
      toast.error("Failed to load organization details");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTransactionFee = async () => {
    if (transactionFee && (isNaN(parseFloat(transactionFee)) || parseFloat(transactionFee) < 0 || parseFloat(transactionFee) > 100)) {
      toast.error("Please enter a valid fee percentage between 0 and 100");
      return;
    }

    setSavingFee(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/organizations/${orgId}/transaction-fee`, {
        transaction_fee_percent: transactionFee ? parseFloat(transactionFee) : null
      }, { headers });
      
      toast.success(transactionFee ? "Custom transaction fee saved" : "Reset to tier default");
      fetchOrgDetails();
    } catch (error) {
      console.error("Error saving fee:", error);
      toast.error("Failed to save transaction fee");
    } finally {
      setSavingFee(false);
    }
  };


  if (loading) {
    return (
      <PageFrame embedded={embedded}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
        </div>
      </PageFrame>
    );
  }

  if (!org) {
    return (
      <PageFrame embedded={embedded}>
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Organization not found</p>
          <Button variant="outline" onClick={() => navigate("/platform/organizations")} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Organizations
          </Button>
        </div>
      </PageFrame>
    );
  }

  let portalPath = null;
  try { portalPath = adminOrgBase(org.subdomain); }
  catch { /* Approved applications can still need a workspace slug. */ }

  return (
    <PageFrame embedded={embedded}>
      <div className="org-detail-page space-y-6" data-testid="org-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/platform/organizations")}
          className="mb-2 -ml-2 text-slate-600 hover:text-slate-900 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Organizations
        </Button>

        <header className="org-detail-header">
          <div className="org-detail-identity">
            <div className="org-detail-logo" style={{ background: "transparent", border: 0, borderRadius: 0, padding: 0 }}>
              {org.logo_url && failedLogo !== org.logo_url ? (
                <img src={org.logo_url} alt={`${org.name} logo`} onError={() => setFailedLogo(org.logo_url)} />
              ) : (
                <Building2 role="img" aria-label="Organization logo unavailable" />
              )}
            </div>
            <div className="org-detail-heading">
              <h1>{org.name}</h1>
                    {org.description && (
                      <p className="text-slate-500 mt-1 max-w-2xl text-sm">{createExcerpt(org.description, 160)}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                      {org.email && (
                        <span className="flex items-center gap-1.5">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {org.email}
                        </span>
                      )}
                      {org.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {org.phone}
                        </span>
                      )}
                      {org.website && (
                        <a
                          href={org.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900"
                        >
                          <Globe className="w-4 h-4 text-slate-400" />
                          Website
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>

              <div className="org-detail-statuses">
                <Badge variant="outline" className="org-detail-status">Organization: {org.status || 'Unknown'}</Badge>
                <Badge variant="outline" className="org-detail-status">
                  <CreditCard aria-hidden="true" className="w-3.5 h-3.5" />
                  Payments: {stats.stripe?.charges_enabled ? 'active' : stats.stripe?.connected ? 'pending' : 'not connected'}
                </Badge>
              </div>
            </div>
          </div>
          <SupportSessionEntry organization={org} />
          <Button variant="outline" onClick={() => navigate(`/admin/organizations/${orgId}/settings`)} data-testid="org-settings-btn">
            <Settings aria-hidden="true" className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </header>

        {stats.stripe && !stats.stripe.charges_enabled && (
          <section className="org-detail-payment-notice" aria-label="Payment setup status">
            <strong><CreditCard aria-hidden="true" className="w-4 h-4" />{stats.stripe.connected ? "Payment setup pending" : "Payments not connected"}</strong>
            <p>Payment setup is separate from this organization’s status.</p>
          </section>
        )}

        {/* Main Stats Row */}
        <div className="org-detail-metrics" aria-label="Organization metrics">
          <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">${stats.totalRevenue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            {stats.pendingPayments > 0 && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                ${stats.pendingPayments.toLocaleString()} pending
              </p>
            )}
          </div>
          
          <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Registrations</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.totalRegistrations}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
              <Clock aria-hidden="true" className="w-3 h-3" />
              {stats.recentRegistrations} in last 30 days
            </p>
          </div>
          
          <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Programs</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.totalPrograms}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-violet-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {stats.activePrograms} active
            </p>
          </div>
          
          <div className="bg-white border border-slate-200/60 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Teams</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.teamsCount}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {stats.coaches} coaches
            </p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="org-detail-columns">
          {/* Programs */}
          <div className="org-detail-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-violet-600" />
                Programs
              </h2>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => navigate("/admin/programs")}
                className="rounded-xl"
              >
                View All
              </Button>
            </div>
            
            {programs.length > 0 ? (
              <div className="space-y-2">
                {programs.slice(0, 5).map(program => (
                  <div
                    key={program.program_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/programs/${program.program_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{program.name}</p>
                        <p className="text-xs text-slate-500">
                          {program.sport} • Ages {program.age_min}-{program.age_max}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize text-xs rounded-lg">
                        {program.status || 'active'}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
                {programs.length > 5 && (
                  <p className="text-center text-xs text-slate-500 pt-2">
                    +{programs.length - 5} more programs
                  </p>
                )}
              </div>
            ) : (
              <div className="org-detail-empty">
                <Trophy className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No programs yet</p>
                <p className="text-sm">Programs will appear here when this organization adds them.</p>
              </div>
            )}
          </div>

          {/* Quick Insights */}
          <div className="org-detail-panel">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Quick Insights
            </h2>
            
            <div className="space-y-4">
              {/* Avg Registration Value */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Avg Registration Value</p>
                    <p className="text-xs text-slate-500">Per player registration</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-slate-900">
                  ${stats.avgRegistrationValue.toFixed(0)}
                </p>
              </div>

              {/* Payment Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Payment Rate</p>
                    <p className="text-xs text-slate-500">{stats.paidCount} paid / {stats.pendingCount} pending</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-slate-900">
                  {stats.totalRegistrations > 0 
                    ? Math.round((stats.paidCount / stats.totalRegistrations) * 100)
                    : 0}%
                </p>
              </div>

              {/* Stripe Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    stats.stripe?.charges_enabled ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    <CreditCard className={`w-5 h-5 ${
                      stats.stripe?.charges_enabled ? 'text-green-600' : 'text-amber-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Stripe Connect</p>
                    <p className="text-xs text-slate-500">Payment processing</p>
                  </div>
                </div>
                <Badge className={`rounded-lg ${
                  stats.stripe?.charges_enabled 
                    ? 'bg-green-100 text-green-700' 
                    : stats.stripe?.connected 
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-slate-100 text-slate-600'
                }`}>
                  {stats.stripe?.charges_enabled ? 'Active' : stats.stripe?.connected ? 'Pending' : 'Not Connected'}
                </Badge>
              </div>

              {/* Organization Age */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Member Since</p>
                    <p className="text-xs text-slate-500">Account created</p>
                  </div>
                </div>
                <p className="text-sm font-medium text-slate-700">
                  {org.created_at ? new Date(org.created_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    year: 'numeric' 
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row - 3 Column */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="org-detail-panel">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-500" />
              Contact Information
            </h3>
            <div className="space-y-3 text-sm">
              {org.address && (
                <div>
                  <p className="text-slate-500">Address</p>
                  <p className="text-slate-900">{org.address}</p>
                </div>
              )}
              {org.email && (
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="text-slate-900">{org.email}</p>
                </div>
              )}
              {org.phone && (
                <div>
                  <p className="text-slate-500">Phone</p>
                  <p className="text-slate-900">{org.phone}</p>
                </div>
              )}
              {org.subdomain && (
                <div>
                  <p className="text-slate-500">Subdomain</p>
                  <p className="text-slate-900 font-mono text-xs">{org.subdomain}.sporthouse.ai</p>
                </div>
              )}
              {!org.address && !org.email && !org.phone && (
                <p className="text-slate-400 text-center py-4">No contact info added</p>
              )}
            </div>
          </div>

          {/* Administrators */}
          <div className="org-detail-panel">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              Administrators ({stats.admins})
            </h3>
            {org.administrators?.length > 0 ? (
              <div className="space-y-2">
                {org.administrators.slice(0, 4).map((admin, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                      <span className="text-violet-600 text-sm font-medium">
                        {admin.name?.charAt(0) || admin.email?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{admin.name || 'Admin'}</p>
                      <p className="text-xs text-slate-500 truncate">{admin.email}</p>
                    </div>
                  </div>
                ))}
                {org.administrators?.length > 4 && (
                  <p className="text-xs text-slate-500 text-center pt-1">
                    +{org.administrators.length - 4} more
                  </p>
                )}
              </div>
            ) : (
              <p className="org-detail-empty text-sm">No administrators</p>
            )}
          </div>

          {/* Transaction Fee */}
          <div className="org-detail-panel">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Percent className="w-4 h-4 text-slate-500" />
              Transaction Fee
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Tier Default:</span>
                  <span className="font-medium text-slate-700">
                    5%
                  </span>
                </div>
                {org.custom_transaction_fee !== undefined && org.custom_transaction_fee !== null && (
                  <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-slate-200">
                    <span className="text-slate-500">Custom:</span>
                    <span className="font-semibold text-emerald-600">{org.custom_transaction_fee}%</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="transactionFee" className="text-xs text-slate-500">Override Fee (%)</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="transactionFee"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={transactionFee}
                      onChange={(e) => setTransactionFee(e.target.value)}
                      placeholder="e.g., 2.5"
                      className="rounded-xl pr-8 text-sm"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  </div>
                  <Button
                    onClick={handleSaveTransactionFee}
                  aria-label="Save transaction fee"
                    disabled={savingFee}
                    className="rounded-xl"
                    size="sm"
                  >
                    {savingFee ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="org-detail-panel">
          <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              variant="outline" 
              className="justify-start rounded-xl h-auto py-3"
              onClick={() => navigate(`/admin/organizations/${orgId}/settings`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              <span className="text-sm">Settings</span>
            </Button>
            <Button 
              variant="outline" 
              className="justify-start rounded-xl h-auto py-3"
              onClick={() => navigate("/admin/programs")}
            >
              <Trophy className="w-4 h-4 mr-2" />
              <span className="text-sm">Programs</span>
            </Button>
            <Button 
              variant="outline" 
              className="justify-start rounded-xl h-auto py-3"
              onClick={() => navigate("/admin/registrations")}
            >
              <FileText className="w-4 h-4 mr-2" />
              <span className="text-sm">Registrations</span>
            </Button>
            <Button 
              variant="outline" 
              className="justify-start rounded-xl h-auto py-3"
              disabled={!portalPath}
              title={!portalPath ? "Set a valid organization subdomain to open the portal" : undefined}
              onClick={() => navigate(portalPath)}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              <span className="text-sm">Public Portal</span>
            </Button>
          </div>
        </div>
      </div>
    </PageFrame>
  );
};

export default AdminOrganizationDetail;
